// models/node.ts

import Document from "./document";
import Asset from "./asset";
import Company from "./company";

// Base interface with common properties
interface BaseNode {
  node_id: number;
  node_name: string;
  children: Node[];
  isExpanded?: boolean;
  isSelected?: boolean;
  isLoading?: boolean;
}

// Type-specific nodes
interface CompanyNode extends BaseNode {
  node_type: "company";
  companyData?: Company;
}

interface AssetNode extends BaseNode {
  node_type: "asset";
  assetData?: Asset;
}

interface DocumentNode extends BaseNode {
  node_type: "document";
  documentData?: Document;
}

// Union type - this is the main Node type
type Node = CompanyNode | AssetNode | DocumentNode;

// Type guards for type safety
export const isCompanyNode = (node: Node): node is CompanyNode => 
  node.node_type === "company";

export const isAssetNode = (node: Node): node is AssetNode => 
  node.node_type === "asset";

export const isDocumentNode = (node: Node): node is DocumentNode => 
  node.node_type === "document";

// Helper to get the data object based on node type
export const getNodeData = (node: Node): Company | Asset | Document | undefined => {
  switch (node.node_type) {
    case "company":
      return (node as CompanyNode).companyData;
    case "asset":
      return (node as AssetNode).assetData;
    case "document":
      return (node as DocumentNode).documentData;
    default:
      return undefined;
  }
};

// Factory functions
export const createCompanyNode = (
  id: number,
  name: string,
  companyData?: Company,
  children: Node[] = []
): CompanyNode => ({
  node_type: "company",
  node_id: id,
  node_name: name,
  companyData,
  children,
  isExpanded: false,
  isSelected: false,
  isLoading: false,
});

export const createAssetNode = (
  id: number,
  name: string,
  assetData?: Asset,
  children: Node[] = []
): AssetNode => ({
  node_type: "asset",
  node_id: id,
  node_name: name,
  assetData,
  children,
  isExpanded: false,
  isSelected: false,
  isLoading: false,
});

export const createDocumentNode = (
  id: number,
  name: string,
  documentData?: Document,
  children: Node[] = []
): DocumentNode => ({
  node_type: "document",
  node_id: id,
  node_name: name,
  documentData,
  children,
  isExpanded: false,
  isSelected: false,
  isLoading: false,
});

export const newNode = (): Node => createCompanyNode(0, "", undefined, []);

export default Node;